import { useMemo } from 'react';
import { useDispatch } from 'react-redux';

import { NotificationService, NOTIFICATION_DURATIONS, NOTIFICATION_TYPE_ERROR } from 'ui-components/services/notification-service';
import { updateSellerSettingsBulk } from '../pages/settings/api/save-settings';
import { saveNewSellerSettings } from '../store/seller/seller.actions';

import { useSellerSetting } from './use-seller-setting';

/**
 * @param {{key: string}} sellerSetting
 * @param {any} defaultValue
 * @param {import('react-intl').MessageDescriptor} [errorMessage]
 * @returns {[any, (newValueOrCallback: any) => Promise<boolean>]}
 */
export const useModifiableSellerSetting = (sellerSetting, defaultValue, errorMessage) => {
  const dispatch = useDispatch();
  const currentSettingValue = useSellerSetting(sellerSetting);
  return useMemo(() => {
    let parsedValue;
    try {
      parsedValue = JSON.parse(currentSettingValue);
    } catch {
      parsedValue = currentSettingValue;
    }
    const valueToUse = parsedValue ?? defaultValue;

    const saveSetting = async (newValueOrCallback) => {
      const newValue = typeof newValueOrCallback === 'function' ? newValueOrCallback(valueToUse) : newValueOrCallback;
      const newValueString = typeof newValue === 'string' ? newValue : JSON.stringify(newValue);
      const postBody = { [sellerSetting.key]: newValueString };
      try {
        const newSettings = await updateSellerSettingsBulk(postBody);
        dispatch(saveNewSellerSettings(newSettings));
        return true;
      } catch {
        if (errorMessage) {
          NotificationService.showNotification({
            notificationType: NOTIFICATION_TYPE_ERROR,
            message: errorMessage,
            duration: NOTIFICATION_DURATIONS.SHORT,
          });
        }
        return false;
      }
    };
    return [valueToUse, saveSetting];
  }, [currentSettingValue, defaultValue, dispatch, errorMessage, sellerSetting.key]);
};
